import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { CalendarEvent, Customer, Ticket } from "../types";
import { parseUtc } from "../utils/datetime";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const REMIND_OPTIONS: Array<{ label: string; minutes: number | null }> = [
  { label: "No reminder", minutes: null },
  { label: "5 min before", minutes: 5 },
  { label: "15 min before", minutes: 15 },
  { label: "30 min before", minutes: 30 },
  { label: "1 hour before", minutes: 60 },
  { label: "1 day before", minutes: 24 * 60 },
];

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmtLocalDateTime(d: Date): string {
  // for <input type="datetime-local">
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface EventForm {
  title: string;
  starts_at: string; // datetime-local string
  ends_at: string; // datetime-local string (optional)
  description: string;
  // Where the technician is going. Rides through to the ICS LOCATION
  // property, which is what makes a dispatched job useful on a phone.
  location: string;
  customer_id: number;
  ticket_id: number;
  remind_minutes_before: number | null;
  // Whose calendar this lands on. 0 = mine.
  user_id: number;
}

type TechUser = { id: number; username: string; role: string; is_active?: boolean };

const blankForm = (anchor: Date): EventForm => {
  const startHour = new Date(anchor);
  startHour.setHours(9, 0, 0, 0);
  const endHour = new Date(anchor);
  endHour.setHours(10, 0, 0, 0);
  return {
    title: "",
    starts_at: fmtLocalDateTime(startHour),
    ends_at: fmtLocalDateTime(endHour),
    description: "",
    location: "",
    customer_id: 0,
    ticket_id: 0,
    remind_minutes_before: 15,
    user_id: 0,
  };
};

export default function Calendar() {
  const [cursor, setCursor] = useState<Date>(startOfMonth(new Date()));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState<EventForm>(blankForm(new Date()));
  const [error, setError] = useState("");
  const [techs, setTechs] = useState<TechUser[]>([]);
  // The dispatch board: everyone's jobs, not just mine.
  const [showEveryone, setShowEveryone] = useState(false);
  const [subscription, setSubscription] = useState<{ url: string; webcal_url: string } | null>(
    null,
  );
  const [showSubscribe, setShowSubscribe] = useState(false);

  const loadEvents = async () => {
    // Pull a wide window covering the displayed grid (~6 weeks)
    const start = new Date(cursor);
    start.setDate(start.getDate() - 7);
    const end = new Date(cursor);
    end.setMonth(end.getMonth() + 1);
    end.setDate(end.getDate() + 7);
    const r = await api.get("/api/v1/calendar", {
      params: {
        start: start.toISOString(),
        end: end.toISOString(),
        ...(showEveryone ? { everyone: true } : {}),
      },
    });
    setEvents(r.data);
  };

  useEffect(() => {
    loadEvents(); /* eslint-disable-next-line */
  }, [cursor, showEveryone]);
  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    api
      .get("/api/v1/tickets", { params: { include_closed: false } })
      .then((r) => setTickets(r.data));
    // Who can be dispatched. A viewer gets a 403 here and simply has no
    // picker, which is correct - they can only book their own time.
    //
    // This read /api/v1/users, which is ADMIN-only, so technicians lost the
    // picker too - and the comment above wrongly assumed only viewers would.
    api
      .get("/api/v1/users/assignable")
      .then((r) =>
        setTechs(
          (r.data || []).filter(
            (u: TechUser) =>
              (u.is_active ?? true) && (u.role === "admin" || u.role === "technician"),
          ),
        ),
      )
      .catch(() => setTechs([]));
  }, []);

  const grid = useMemo(() => {
    // Build a 6x7 grid starting on the Sunday on/before the 1st of the cursor month.
    const first = startOfMonth(cursor);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const d = parseUtc(e.starts_at);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    map.forEach((arr) => arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at)));
    return map;
  }, [events]);

  const daysEvents = (d: Date) =>
    eventsByDay.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) || [];

  const today = new Date();
  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });

  const openCreate = (day?: Date) => {
    const anchor = day || selectedDay;
    setEditing(null);
    setForm(blankForm(anchor));
    setError("");
    setShowForm(true);
  };

  const openEdit = (e: CalendarEvent) => {
    setEditing(e);
    setForm({
      title: e.title,
      starts_at: fmtLocalDateTime(parseUtc(e.starts_at) ?? new Date()),
      ends_at: e.ends_at ? fmtLocalDateTime(parseUtc(e.ends_at) ?? new Date()) : "",
      description: e.description || "",
      location: (e as any).location || "",
      customer_id: e.customer_id || 0,
      ticket_id: e.ticket_id || 0,
      remind_minutes_before: e.remind_minutes_before ?? null,
      user_id: (e as any).user_id || 0,
    });
    setError("");
    setShowForm(true);
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    try {
      const payload: any = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        customer_id: form.customer_id || null,
        ticket_id: form.ticket_id || null,
        remind_minutes_before: form.remind_minutes_before,
        // Omitted entirely when unset, so the server applies its "me" default
        // rather than being handed a 0 that matches no user.
        ...(form.user_id ? { user_id: form.user_id } : {}),
      };
      if (editing) {
        await api.put(`/api/v1/calendar/${editing.id}`, payload);
      } else {
        await api.post("/api/v1/calendar", payload);
      }
      setShowForm(false);
      setEditing(null);
      loadEvents();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteEvent = async (e: CalendarEvent) => {
    if (!window.confirm(`Delete event "${e.title}"?`)) return;
    await api.delete(`/api/v1/calendar/${e.id}`);
    setShowForm(false);
    setEditing(null);
    loadEvents();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Schedule</h1>
          {techs.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={showEveryone}
                onChange={(e) => setShowEveryone(e.target.checked)}
              />
              Everyone
            </label>
          )}
          <button
            onClick={async () => {
              if (!subscription) {
                const r = await api.get("/api/v1/calendar/subscription");
                setSubscription(r.data);
              }
              setShowSubscribe(true);
            }}
            className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Subscribe in Outlook
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor(addMonths(cursor, -1))}
            className="border dark:border-gray-600 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 dark:text-gray-200"
          >
            &larr;
          </button>
          <button
            onClick={() => setCursor(startOfMonth(new Date()))}
            className="border dark:border-gray-600 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 dark:text-gray-200"
          >
            Today
          </button>
          <button
            onClick={() => setCursor(addMonths(cursor, 1))}
            className="border dark:border-gray-600 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 dark:text-gray-200"
          >
            &rarr;
          </button>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-3 min-w-[160px]">{monthLabel}</div>
          <button
            onClick={() => openCreate(selectedDay)}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 ml-2"
          >
            New Event
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Month grid */}
        <div className="col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-center"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {grid.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = isSameDay(d, today);
              const isSelected = isSameDay(d, selectedDay);
              const dayEvents = daysEvents(d);
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(d)}
                  className={`text-left p-2 min-h-[88px] border-r border-b border-gray-100 dark:border-gray-700 transition-colors
                    ${inMonth ? "bg-white dark:bg-gray-800" : "bg-gray-50/60 dark:bg-gray-900/40"}
                    ${isSelected ? "ring-2 ring-blue-500 ring-inset" : ""}
                    ${isToday ? "bg-blue-50 dark:bg-blue-900/30" : ""}
                    hover:bg-blue-50/50 dark:hover:bg-blue-900/20`}
                >
                  <div
                    className={`text-xs font-medium mb-1 ${isToday ? "text-blue-600 dark:text-blue-400" : inMonth ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"}`}
                  >
                    {d.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <div
                        key={e.id}
                        className="truncate text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300"
                        title={e.title}
                      >
                        {(parseUtc(e.starts_at) ?? new Date()).toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                        })}{" "}
                        {e.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 px-1.5">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected-day panel */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 h-fit">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {selectedDay.toLocaleString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </h2>
            <button
              onClick={() => openCreate(selectedDay)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              + Add
            </button>
          </div>
          {daysEvents(selectedDay).length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No events on this day.</p>
          )}
          <div className="space-y-2">
            {daysEvents(selectedDay).map((e) => (
              <div
                key={e.id}
                onClick={() => openEdit(e)}
                className="border dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
              >
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {(parseUtc(e.starts_at) ?? new Date()).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                  {e.ends_at &&
                    ` – ${(parseUtc(e.ends_at) ?? new Date()).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{e.title}</div>
                {(e.customer_name || e.ticket_title) && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {e.customer_name}
                    {e.customer_name && e.ticket_title ? " · " : ""}
                    {e.ticket_title}
                  </div>
                )}
                {/* Only when looking at the whole team — on your own calendar
                    every row would say your own name. */}
                {showEveryone && (e as any).user_username && (
                  <div className="text-xs text-brand dark:text-brand-light mt-0.5">{(e as any).user_username}</div>
                )}
                {(e as any).location && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    📍 {(e as any).location}
                  </div>
                )}
                {e.remind_minutes_before != null && (
                  <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
                    🔔{" "}
                    {REMIND_OPTIONS.find((r) => r.minutes === e.remind_minutes_before)?.label ||
                      `${e.remind_minutes_before} min before`}
                    {e.reminded_at ? " (sent)" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{editing ? "Edit Event" : "New Event"}</h2>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  autoFocus
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Starts</label>
                  <input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ends</label>
                  <input
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
              </div>
              {techs.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Assign to
                  </label>
                  <select
                    value={form.user_id}
                    onChange={(e) => setForm({ ...form, user_id: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value={0}>Me</option>
                    {techs.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The job lands on their calendar and their Outlook subscription, and they
                    are emailed.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Location (opt.)
                </label>
                <input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Site address, or where to meet"
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Customer (opt.)</label>
                  <select
                    value={form.customer_id}
                    onChange={(e) =>
                      setForm({ ...form, customer_id: Number(e.target.value), ticket_id: 0 })
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value={0}>—</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ticket (opt.)</label>
                  <select
                    value={form.ticket_id}
                    onChange={(e) => setForm({ ...form, ticket_id: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value={0}>—</option>
                    {tickets
                      .filter((t) => !form.customer_id || t.customer_id === form.customer_id)
                      .slice(0, 50)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          #{t.id} {t.title}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Reminder</label>
                <select
                  value={
                    form.remind_minutes_before == null ? "null" : String(form.remind_minutes_before)
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      remind_minutes_before:
                        e.target.value === "null" ? null : Number(e.target.value),
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  {REMIND_OPTIONS.map((r) => (
                    <option
                      key={String(r.minutes)}
                      value={r.minutes == null ? "null" : String(r.minutes)}
                    >
                      {r.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Email is sent to your account email at the reminder time.
                </p>
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex justify-between items-center pt-2">
                {editing ? (
                  <button
                    type="button"
                    onClick={() => deleteEvent(editing)}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Delete
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditing(null);
                    }}
                    className="border dark:border-gray-600 text-sm px-4 py-2 rounded-lg dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
                  >
                    {editing ? "Save" : "Create"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubscribe && subscription && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Subscribe to your schedule
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                A live subscription, not a one-off download — jobs added or moved here update
                in Outlook on its own refresh.
              </p>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Subscription URL
                </div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={subscription.url}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={() => navigator.clipboard?.writeText(subscription.url)}
                    className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 shrink-0"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <ol className="list-decimal ml-5 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <li>
                  In Outlook, go to <strong>Add calendar → Subscribe from web</strong> and paste
                  the URL above.
                </li>
                <li>
                  Or open{" "}
                  <a
                    href={subscription.webcal_url}
                    className="text-blue-600 dark:text-blue-400 hover:underline break-all font-medium"
                  >
                    the webcal link
                  </a>{" "}
                  to let your default calendar app set it up.
                </li>
              </ol>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Treat this URL like a password — anyone holding it can read your schedule
                without signing in. Rotating it below immediately breaks every copy.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-between">
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Issue a new URL? Every calendar already subscribed to the old one will stop updating until it is re-added.",
                    )
                  )
                    return;
                  const r = await api.post("/api/v1/calendar/subscription/rotate");
                  setSubscription(r.data);
                }}
                className="text-xs text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Rotate URL
              </button>
              <button
                onClick={() => setShowSubscribe(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
